import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import Table from './Table';
import formatDate from '../helpers/formatDate';
import { DateTime } from 'luxon';

export default function SentEmailsTable() {
  const [ffData, setFFData] = useState([]);
  const [loadingFFData, setLoadingFFData] = useState(false);

  const getURLParams = () => new URLSearchParams(window.location.search);

  const getStartDate = () =>
    getURLParams().get('start_date') || DateTime.now().startOf('day').toISO().slice(0, 16);

  const getEndDate = () =>
    getURLParams().get('end_date') || DateTime.now().endOf('day').toISO().slice(0, 16);

  const setDateParams = (start, end) => {
    const params = new URLSearchParams(window.location.search);
    params.set('start_date', start);
    params.set('end_date', end);
    window.history.pushState({}, '', `?${params.toString()}`);
    setFilter(start + end);
  };

  const [filter, setFilter] = useState(getStartDate() + getEndDate());

  useEffect(() => {
    setLoadingFFData(true);
    async function fetchData() {
      const searchParams = new URLSearchParams({
        start_date: getStartDate(),
        end_date: getEndDate(),
      });
      const response = await fetch("api/sent_emails?" + searchParams);

      const res = await response.json();
      return res;
    }
    fetchData()
      .then((res) => {
        setFFData(res.data);
        setLoadingFFData(false);
      })
      .catch(console.error);
  }, [filter]);

    const columns = React.useMemo(
      () => [
        {
          accessorFn: (row) => row.id?.toString() || '',
          header: 'ID',
        },
        {
          accessorFn: (row) => formatDate(row.created_at),
          header: 'Timestamp',
        },
        {
          accessorFn: (row) => row.email_type,
          header: 'Email Type',
        },
        {
          header: 'Recipient Type',
          accessorFn: (row) => {
            if (row.broker_id) return 'Broker';
            if (row.lead_owner_id) return 'Lead Owner';
            if (row.admin_id) return 'Admin';
            if (row.brand_admin_id) return 'Brand Admin';
            return null;
          },
        },
        {
          header: 'Recipient ID',
          accessorFn: (row) => {
            if (row.broker_id) return row.broker_id;
            if (row.lead_owner_id) return row.lead_owner_id;
            if (row.admin_id) return row.admin_id;
            if (row.brand_admin_id) return row.brand_admin_id;
            return null;
          },
        },
        {
          header: 'Recipient Name',
          accessorFn: (row) => {
            if (row.broker_name) return row.broker_name;
            if (row.lead_owner_name) return row.lead_owner_name;
            if (row.admin_name) return row.admin_name;
            if (row.brand_admin_name) return row.brand_admin_name;
            return null;
          },
        },
        {
          header: 'Recipient Email',
          accessorFn: (row) => {
            if (row.broker_email) return row.broker_email;
            if (row.lead_owner_email) return row.lead_owner_email;
            if (row.admin_email) return row.admin_email;
            if (row.brand_admin_email) return row.brand_admin_email;
            return null;
          },
          cell: (info) => {
            const value = info.getValue();
            if (!value) return null;

            return (
              <span>
                {value}
                {info.row.original.recipient_email_current_mismatch && (
                  <span
                    className="border px-1"
                    style={{
                      marginLeft: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                      color: '#c00',
                      fontWeight: 'bold',
                    }}
                    title="This recipient's current email address is different from the one this email was actually sent to."
                  >
                    !
                  </span>
                )}
              </span>
            );
          },
        },
        {
          header: 'Sender Email',
          accessorFn: (row) => row.sender_email || '',
        },
        {
          header: 'From Domain',
          accessorFn: (row) => row.from_domain_status || '',
          meta: { filterVariant: 'select' },
          cell: (info) => {
            const status = info.getValue();
            if (!status) return null;

            const isMismatched = status === 'mismatched';
            return (
              <span
                className="border px-1"
                style={{
                  borderRadius: '3px',
                  backgroundColor: isMismatched ? 'rgba(255, 0, 0, 0.1)' : status === 'matched' ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 255, 0, 0.1)',
                  whiteSpace: 'nowrap',
                }}
                title="Whether the sender's email domain was SendGrid-verified at the time this email was sent."
              >
                {status}
              </span>
            );
          },
        },
                {
          header: 'Engagement ID',
          accessorFn: (row) => row.engagement_id,
        },
        {
          header: 'Engagement Title',
          accessorFn: (row) => row.engagement_title,
        },
      ],
      []
    );

	return (
    <>
      <div style={{ marginBottom: '10px', border: '1px solid #ccc', padding: '10px', display: 'flex', gap: '20px' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ height: '30px', marginTop: '20px' }}
          onClick={() => {
            const newStartDate = DateTime.fromISO(getStartDate()).minus({ days: 1 }).toISO().slice(0, 16);
            const newEndDate = DateTime.fromISO(newStartDate).endOf('day').toISO().slice(0, 16);
            setDateParams(newStartDate, newEndDate);
          }}
        >
          &#8592; Previous Day
        </button>
        <div>
          <label htmlFor="start-datetime">HS Updated At Start: </label>
          <input
            id="start-datetime"
            disabled
            type="datetime-local"
            value={getStartDate()}
            onChange={(event) => setDateParams(event.target.value, getEndDate())}
          />
        </div>
        <div>
          <label htmlFor="end-datetime">HS Updated At End: </label>
          <input
            id="end-datetime"
            disabled
            type="datetime-local"
            value={getEndDate()}
            onChange={(event) => setDateParams(getStartDate(), event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ height: '30px', marginTop: '20px' }}
          onClick={() => {
            const newStartDate = DateTime.fromISO(getStartDate()).plus({ days: 1 }).toISO().slice(0, 16);
            const newEndDate = DateTime.fromISO(newStartDate).endOf('day').toISO().slice(0, 16);
            setDateParams(newStartDate, newEndDate);
          }}
        >
          Next Day &#8594;
        </button>
      </div>
      <div className="border bg-white p-3">
        <Table
          loadingFFData={loadingFFData}
          data={ffData}
          columns={columns}
        />
      </div>
    </>

  );
};

const element = document.getElementById('sent-emails-table');
if (element) {
  const root = createRoot(element);
  root.render(<SentEmailsTable/>);
}
